import { useState, useEffect } from "react";
import "@/App.css";
import { 
  Phone, 
  MessageCircle, 
  Car, 
  Truck, 
  Clock, 
  MapPin, 
  Star, 
  Wrench, 
  Key, 
  Menu, 
  X,
  ChevronRight,
  Shield,
  Users,
  Zap,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";

// Constants
const WHATSAPP_NUMBER = "5551998090431";
const WHATSAPP_MESSAGE = "Olá! Preciso de um guincho.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const PHONES = {
  primary: "(51) 99809-0431",
  primaryTel: "5551998090431",
  secondary: "(51) 98302-4444",
  secondaryTel: "5551983024444", 
  landline: "(51) 3671-9733",
  landlineTel: "555136719733"
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_18ae1f84-5262-42c3-822c-786ed75690f1/artifacts/da857sjt_WhatsApp_Image_2026-03-22_at_13.56.14-removebg-preview.png";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_18ae1f84-5262-42c3-822c-786ed75690f1/artifacts/w74qrx3v_WhatsApp%20Image%202026-03-22%20at%2014.01.56.jpeg";

const GALLERY_IMAGES = [
  "https://customer-assets.emergentagent.com/job_18ae1f84-5262-42c3-822c-786ed75690f1/artifacts/x1pd44sm_WhatsApp%20Image%202026-03-22%20at%2014.01.55-2.jpeg",
  "https://customer-assets.emergentagent.com/job_18ae1f84-5262-42c3-822c-786ed75690f1/artifacts/zezci4o0_WhatsApp%20Image%202026-03-22%20at%2014.01.55-4.jpeg",
  "https://customer-assets.emergentagent.com/job_18ae1f84-5262-42c3-822c-786ed75690f1/artifacts/nz3kqitg_WhatsApp%20Image%202026-03-22%20at%2014.38.09.jpeg"
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicos", label: "Serviços" },
    { href: "#diferenciais", label: "Diferenciais" },
    { href: "#area", label: "Área de Atendimento" },
    { href: "#avaliacoes", label: "Avaliações" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Mobile Menu Button - Left on mobile */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-president-dark"
            data-testid="mobile-menu-button"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Logo - Centered on mobile */}
          <a href="#" className="flex items-center absolute left-1/2 -translate-x-1/2 md:relative md:left-0 md:translate-x-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Guinchos Presidente" 
              className="h-12 md:h-16 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-president-dark hover:text-president-blue font-medium transition-colors"
                data-testid={`nav-${link.href.replace('#', '')}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${PHONES.primaryTel}`}
              className="flex items-center gap-2 text-president-dark hover:text-president-blue font-semibold transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-5 h-5" />
              <span className="hidden xl:inline">{PHONES.primary}</span>
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-president-yellow text-president-dark px-5 py-2.5 rounded-full font-bold hover:brightness-110 transition-all btn-yellow-glow"
              data-testid="header-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Empty div for mobile spacing */}
          <div className="w-10 lg:hidden"></div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 mobile-menu-enter" data-testid="mobile-menu">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-president-dark hover:text-president-blue font-medium py-2 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 pt-3 border-t border-gray-100">
                <a
                  href={`tel:${PHONES.primaryTel}`}
                  className="flex items-center justify-center gap-2 bg-president-blue text-white px-5 py-3 rounded-full font-bold"
                  data-testid="mobile-phone"
                >
                  <Phone className="w-5 h-5" />
                  Ligar Agora
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-president-yellow text-president-dark px-5 py-3 rounded-full font-bold"
                  data-testid="mobile-whatsapp"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center pt-20"
      style={{
        backgroundImage: `url(${HERO_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-overlay" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 w-full">
        <div className="max-w-3xl mx-auto text-center md:text-left md:mx-0">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-president-yellow/20 border border-president-yellow/40 text-president-yellow px-4 py-2 rounded-full mb-6 animate-fade-in-up">
            <Clock className="w-4 h-4" />
            <span className="font-semibold text-sm">Atendimento 24 horas</span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight mb-4 animate-fade-in-up animation-delay-100">
            <span className="text-president-blue">Guinchos</span><br />
            <span className="text-president-yellow">Presidente 24H</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-200 mb-4 animate-fade-in-up animation-delay-200">
            Guincho leve e pesado com atendimento imediato na região
          </p>

          {/* Location */}
          <p className="text-base md:text-lg text-gray-300 mb-8 flex items-center justify-center md:justify-start gap-2 animate-fade-in-up animation-delay-300">
            <MapPin className="w-5 h-5 text-president-yellow flex-shrink-0" />
            Camaquã, Tapes, Cristal, São Lourenço do Sul e Arambaré
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-fade-in-up animation-delay-400">
            <a
              href={`tel:${PHONES.primaryTel}`}
              className="flex items-center justify-center gap-3 bg-president-yellow text-president-dark px-8 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all btn-yellow-glow"
              data-testid="hero-call-btn"
            >
              <Phone className="w-6 h-6" />
              Ligar Agora
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 bg-whatsapp text-white px-8 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle className="w-6 h-6" />
              WhatsApp
            </a>
          </div>

          {/* Phone Numbers */}
          <div className="mt-10 flex flex-wrap justify-center md:justify-start gap-6 text-white animate-fade-in-up animation-delay-500">
            <a href={`tel:${PHONES.primaryTel}`} className="flex items-center gap-2 phone-link" data-testid="phone-1">
              <Phone className="w-5 h-5 text-president-yellow" />
              <span className="font-semibold">{PHONES.primary}</span>
            </a>
            <a href={`tel:${PHONES.secondaryTel}`} className="flex items-center gap-2 phone-link" data-testid="phone-2">
              <Phone className="w-5 h-5 text-president-yellow" />
              <span className="font-semibold">{PHONES.secondary}</span>
            </a>
            <a href={`tel:${PHONES.landlineTel}`} className="flex items-center gap-2 phone-link" data-testid="phone-3">
              <Phone className="w-5 h-5 text-president-yellow" />
              <span className="font-semibold">{PHONES.landline}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Car,
      title: "Guincho Leve",
      description: "Transporte seguro para carros, motos e utilitários com atendimento rápido 24 horas."
    },
    {
      icon: Truck,
      title: "Guincho Pesado",
      description: "Remoção e transporte de caminhões, máquinas e veículos pesados com total segurança."
    },
    {
      icon: Car,
      title: "Táxi 24h",
      description: "Transporte rápido para passageiros em qualquer horário."
    },
    {
      icon: Key,
      title: "Aluguel de Veículos",
      description: "Veículos disponíveis para locação imediata."
    },
    {
      icon: Wrench,
      title: "Oficina 24h",
      description: "Suporte mecânico emergencial com atendimento rápido."
    }
  ];

  return (
    <section id="servicos" className="py-16 md:py-24 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-president-dark uppercase tracking-tight mb-4">
            Nossos Serviços
          </h2>
          <div className="section-divider mx-auto mb-4" />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Atendimento completo 24 horas para qualquer emergência na estrada
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="service-card bg-white border border-gray-100 rounded-2xl p-6 md:p-8 hover:border-president-blue/30"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-president-blue/10 rounded-xl flex items-center justify-center mb-5">
                <service.icon className="w-7 h-7 text-president-blue" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-president-dark mb-3">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Heavy Truck Section
const HeavyTruckSection = () => {
  const vehicles = [
    "Caminhões", "Carretas", "Bitrem", "Ônibus", "Vans", "Micro-ônibus",
    "Máquinas agrícolas", "Máquinas pesadas", "Implementos rodoviários",
    "Caminhão baú", "Caminhão tanque"
  ];

  const situations = [
    "Pane mecânica", "Acidente", "Veículo atolado", "Problema elétrico",
    "Falta de combustível", "Transporte programado", "Remoção de emergência"
  ];

  return (
    <section className="py-16 md:py-24 bg-president-blue cta-pattern text-white" data-testid="heavy-truck-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full mb-6">
              <Truck className="w-4 h-4" />
              <span className="font-semibold text-sm">Especialidade</span>
            </div>

            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mb-6">
              Guincho para<br />
              <span className="text-white">Veículos Pesados</span>
            </h2>

            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              Atendimento especializado para veículos pesados com equipamentos adequados e equipe experiente. 
              A Guinchos Presidente oferece remoção segura e rápida em toda a região.
            </p>

            <div className="flex items-center gap-3 mb-10">
              <AlertTriangle className="w-6 h-6 text-white" />
              <span className="text-xl font-bold">Atendimento emergencial 24 horas</span>
            </div>

            {/* Two columns */}
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading text-xl font-bold text-white mb-4 uppercase">
                  Atendemos
                </h3>
                <ul className="space-y-2">
                  {vehicles.map((vehicle, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-200">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      {vehicle}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-white mb-4 uppercase">
                  Situações atendidas
                </h3>
                <ul className="space-y-2">
                  {situations.map((situation, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-200">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      {situation}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="grid grid-cols-2 gap-4">
            {GALLERY_IMAGES.slice(0, 2).map((image, index) => (
              <div key={index} className="overflow-hidden rounded-2xl">
                <img
                  src={image}
                  alt={`Guincho pesado ${index + 1}`}
                  className="w-full h-48 md:h-64 object-cover gallery-image"
                />
              </div>
            ))}
            <div className="col-span-2 overflow-hidden rounded-2xl">
              <img
                src={GALLERY_IMAGES[2]}
                alt="Guincho pesado 3"
                className="w-full h-48 md:h-64 object-cover gallery-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Differentials Section
const Differentials = () => {
  const items = [
    { icon: Clock, title: "Atendimento 24 horas" },
    { icon: Truck, title: "Guincho leve e pesado" },
    { icon: Zap, title: "Chegada rápida" },
    { icon: Users, title: "Equipe especializada" },
    { icon: MapPin, title: "Cobertura regional" },
    { icon: Shield, title: "Preço justo" },
    { icon: Phone, title: "Atendimento imediato" }
  ];

  return (
    <section id="diferenciais" className="py-16 md:py-24 bg-gray-50" data-testid="differentials-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-president-dark uppercase tracking-tight mb-4">
            Nossos Diferenciais
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 text-center border border-gray-100 hover:border-president-yellow/50 transition-all hover:shadow-lg"
              data-testid={`differential-${index}`}
            >
              <div className="w-12 h-12 bg-president-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <item.icon className="w-6 h-6 text-president-yellow" />
              </div>
              <h3 className="font-bold text-president-dark">{item.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Coverage Area Section
const CoverageArea = () => {
  const cities = [
    "Camaquã",
    "Tapes",
    "Cristal",
    "São Lourenço do Sul",
    "Arambaré",
    "Região próxima"
  ];

  return (
    <section id="area" className="py-16 md:py-24 bg-white" data-testid="coverage-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-president-dark uppercase tracking-tight mb-4">
              Área de Atendimento
            </h2>
            <div className="section-divider mb-6" />
            <p className="text-lg text-gray-600 mb-8">
              Atendimento rápido em toda a região sul do RS. Equipe disponível 24 horas para qualquer emergência.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {cities.map((city, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 bg-president-blue/5 rounded-lg p-4 hover:bg-president-blue/10 transition-colors"
                  data-testid={`city-${index}`}
                >
                  <MapPin className="w-5 h-5 text-president-blue flex-shrink-0" />
                  <span className="font-semibold text-president-dark">{city}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-president-blue rounded-2xl p-8 text-white">
            <h3 className="font-heading text-2xl font-bold mb-4">Precisa de Guincho?</h3>
            <p className="text-blue-100 mb-6">
              Entre em contato agora mesmo. Atendimento imediato 24 horas.
            </p>
            <div className="space-y-3">
              <a
                href={`tel:${PHONES.primaryTel}`}
                className="flex items-center justify-center gap-3 bg-president-yellow text-president-dark px-6 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all w-full"
                data-testid="coverage-call-btn"
              >
                <Phone className="w-5 h-5" />
                {PHONES.primary}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-whatsapp text-white px-6 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all w-full"
                data-testid="coverage-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Chamar no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const Reviews = () => {
  const reviews = [
    {
      name: "Carlos M.",
      text: "Atendimento super rápido, chegou em menos de 30 minutos.",
      rating: 5
    },
    {
      name: "Roberto A.",
      text: "Precisei de guincho pesado e resolveram na hora.",
      rating: 5
    },
    {
      name: "Juliana S.",
      text: "Serviço confiável e preço justo, recomendo.",
      rating: 5
    }
  ];

  return (
    <section id="avaliacoes" className="py-16 md:py-24 bg-gray-50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-president-dark uppercase tracking-tight mb-4">
            Avaliações
          </h2>
          <div className="section-divider mx-auto" />
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <div
              key={index}
              className="review-card rounded-2xl p-6 md:p-8"
              data-testid={`review-${index}`}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-president-yellow text-president-yellow" />
                ))}
              </div>
              
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                "{review.text}"
              </p>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-president-blue rounded-full flex items-center justify-center text-white font-bold">
                  {review.name.charAt(0)}
                </div>
                <span className="font-semibold text-president-dark">{review.name}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="py-16 md:py-24 bg-president-blue cta-pattern" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-4">
          Precisa de Guincho Agora?
        </h2>
        <p className="text-xl text-blue-100 mb-10">
          Atendimento imediato 24 horas. Ligue ou chame no WhatsApp.
        </p>

        {/* Phone Numbers */}
        <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 mb-10">
          <a
            href={`tel:${PHONES.primaryTel}`}
            className="flex items-center justify-center gap-3 text-white text-xl md:text-2xl font-bold hover:text-president-yellow transition-colors"
            data-testid="cta-phone-1"
          >
            <Phone className="w-6 h-6" />
            {PHONES.primary}
          </a>
          <a
            href={`tel:${PHONES.secondaryTel}`}
            className="flex items-center justify-center gap-3 text-white text-xl md:text-2xl font-bold hover:text-president-yellow transition-colors"
            data-testid="cta-phone-2"
          >
            <MessageCircle className="w-6 h-6" />
            {PHONES.secondary}
          </a>
          <a
            href={`tel:${PHONES.landlineTel}`}
            className="flex items-center justify-center gap-3 text-white text-xl md:text-2xl font-bold hover:text-president-yellow transition-colors"
            data-testid="cta-phone-3"
          >
            <Phone className="w-6 h-6" />
            {PHONES.landline}
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a
            href={`tel:${PHONES.primaryTel}`}
            className="flex items-center justify-center gap-3 bg-president-yellow text-president-dark px-8 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all btn-yellow-glow"
            data-testid="cta-call-btn"
          >
            <Phone className="w-6 h-6" />
            Ligar Agora
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 bg-whatsapp text-white px-8 py-4 rounded-full font-bold text-lg hover:brightness-110 transition-all"
            data-testid="cta-whatsapp-btn"
          >
            <MessageCircle className="w-6 h-6" />
            WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const services = [
    "Guincho leve",
    "Guincho pesado",
    "Táxi 24h",
    "Oficina 24h",
    "Aluguel de veículos"
  ];

  return (
    <footer className="bg-president-yellow text-president-dark py-12 md:py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-3 gap-10 mb-10">
          {/* Logo & Description */}
          <div>
            <img src={LOGO_URL} alt="Guinchos Presidente" className="h-16 mb-4" />
            <p className="text-president-dark/70">
              Guincho 24 horas na região sul do RS. Atendimento rápido e seguro.
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 uppercase">Serviços</h3>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <a href="#servicos" className="text-president-dark/70 hover:text-president-dark transition-colors flex items-center gap-2">
                    <ChevronRight className="w-4 h-4" />
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading text-xl font-bold mb-4 uppercase">Telefones</h3>
            <div className="space-y-3">
              <a href={`tel:${PHONES.primaryTel}`} className="flex items-center gap-3 text-president-dark/70 hover:text-president-dark transition-colors">
                <Phone className="w-5 h-5" />
                {PHONES.primary}
              </a>
              <a href={`tel:${PHONES.secondaryTel}`} className="flex items-center gap-3 text-president-dark/70 hover:text-president-dark transition-colors">
                <Phone className="w-5 h-5" />
                {PHONES.secondary}
              </a>
              <a href={`tel:${PHONES.landlineTel}`} className="flex items-center gap-3 text-president-dark/70 hover:text-president-dark transition-colors">
                <Phone className="w-5 h-5" />
                {PHONES.landline}
              </a>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 bg-president-dark/10 text-president-dark px-4 py-2 rounded-full">
              <Clock className="w-4 h-4" />
              <span className="font-semibold text-sm">Atendimento 24 horas</span>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-president-dark/20 pt-8 text-center text-president-dark/60">
          <p>© {new Date().getFullYear()} Guinchos Presidente. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-whatsapp rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform whatsapp-pulse"
      data-testid="floating-whatsapp"
      aria-label="Chamar no WhatsApp"
    >
      <MessageCircle className="w-8 h-8 text-white" />
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="font-body">
      <Header />
      <main>
        <Hero />
        <Services />
        <HeavyTruckSection />
        <Differentials />
        <CoverageArea />
        <Reviews />
        <CTASection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
